import {
  enumInventoryMovementStatus,
  enumUnit,
  InventoryMovementSortOption,
  InventoryTransactionType,
  Unit,
} from '@rematter/frontend-lib-api-client';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export type MovementForAllocation = {
  id: string;
  itemId: string;
  itemName: string;
  facilityId: string;
  facilityName: string;
  quantity: number;
  quantityUnit: Unit;
  allocatedQuantity: number;
  unallocatedQuantity: number;
  allocatedQuantityToTags: number;
  unallocatedQuantityFromTags: number;
  occurredAt: string;
  originTransactionType: InventoryTransactionType;
  originTransactionItemId: string;
  ownerAccountId?: string;
  ownerAccountName?: string;
};

export const movementsForAllocationQueryAtom = createQueryAtomFactory(
  'inventory.movementsForAllocation',
  (params: {
    facilityId: string;
    itemId?: string;
    limit?: number;
    offset?: number;
    sorting?: InventoryMovementSortOption[];
  }) => ({
    inventoryMovements: {
      __args: {
        filter: {
          facilityId: params.facilityId,
          itemId: params.itemId,
          status: [enumInventoryMovementStatus.Completed],
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 50,
          offset: params.offset ?? 0,
        },
      },
      entries: {
        id: true,
        itemId: true,
        item: {
          id: true,
          name: true,
        },
        facilityId: true,
        facility: {
          id: true,
          facilityName: true,
        },
        quantity: {
          value: true,
          unit: true,
        },
        allocatedQuantity: {
          value: true,
          unit: true,
        },
        unallocatedQuantity: {
          value: true,
          unit: true,
        },
        allocatedQuantityToTags: {
          value: true,
          unit: true,
        },
        unallocatedQuantityFromTags: {
          value: true,
          unit: true,
        },
        occurredAt: true,
        originTransactionType: true,
        originTransactionItemId: true,
        ownerAccountId: true,
        ownerAccount: {
          id: true,
          name: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (
      data,
    ): {
      movements: MovementForAllocation[];
      totalCount: number;
    } => {
      if (
        !data ||
        !data.inventoryMovements ||
        !data.inventoryMovements.entries
      ) {
        return {
          movements: [],
          totalCount: 0,
        };
      }

      return {
        movements: data.inventoryMovements.entries
          .filter((movement) => {
            const unallocated =
              Number(movement.unallocatedQuantity?.value) || 0;
            return unallocated > 0;
          })
          .map((movement) => ({
            id: movement.id,
            itemId: movement.itemId,
            itemName: movement.item?.name || '',
            facilityId: movement.facilityId,
            facilityName: movement.facility?.facilityName || '',
            quantity: Number(movement.quantity?.value) || 0,
            quantityUnit: movement.quantity?.unit || enumUnit.Pounds,
            allocatedQuantity: Number(movement.allocatedQuantity?.value) || 0,
            unallocatedQuantity:
              Number(movement.unallocatedQuantity?.value) || 0,
            allocatedQuantityToTags:
              Number(movement.allocatedQuantityToTags?.value) || 0,
            unallocatedQuantityFromTags:
              Number(movement.unallocatedQuantityFromTags?.value) || 0,
            occurredAt: movement.occurredAt,
            originTransactionType: movement.originTransactionType,
            originTransactionItemId: movement.originTransactionItemId,
            ownerAccountId: movement.ownerAccountId || undefined,
            ownerAccountName: movement.ownerAccount?.name || undefined,
          })),
        totalCount: data.inventoryMovements.page?.total || 0,
      };
    },
  },
);
